// FAQ semantic search: every entry is vectorised into a TF-IDF index over a real vocabulary
// (no hashing, so unrelated words never collide), and queries are ranked by cosine similarity.
// Queries are expanded with synonyms, partial words (while typing) and near-miss spellings.
// Pure functions only, so the ranking can be tested without a browser.

const STOPWORDS = new Set(("a about an and any are as at be by can could do does doing for from get got has have " +
  "how i if in into is it its me my of on or our should so that the their them then there these this those to us " +
  "use using was we what when where which whom why will with would you your yours").split(" "))

// Each group is treated as one concept: a query word matches documents that use any word in its group.
const SYNONYM_GROUPS = [
  ["secure", "security", "safe", "safety", "privacy", "private", "protect", "protection", "confidential", "confidentiality"],
  ["price", "pricing", "cost", "fee", "charge", "pay", "paid", "plan", "subscription", "expensive", "cheap", "afford"],
  ["free", "trial", "demo", "try", "test"],
  ["fast", "speed", "quick", "quickly", "long", "time", "second", "minute"],
  ["accurate", "accuracy", "precise", "correct", "reliable"],
  ["help", "support", "contact", "email", "assistance"],
  ["start", "begin", "signup", "sign", "register", "registration", "account", "onboard"],
  ["upload", "import", "file", "format", "excel", "xlsx", "csv", "spreadsheet"],
  ["erp", "software", "system", "integrate", "integration", "quickbooks", "xero", "sage", "compatible", "compatibility"],
  ["delete", "deletion", "purge", "purging", "retain", "retention", "store", "storage"],
  ["partial", "deficit", "split", "installment"],
  ["fuzzy", "similar", "approximate"],
  ["who", "audience", "intended", "suitable", "role"],
  ["work", "process", "step", "procedure"],
]

const MIN_SCORE = 0.12
const RELATIVE_CUTOFF = 0.35
const EXACT_WEIGHT = 1
const SYNONYM_WEIGHT = 0.6
const PREFIX_WEIGHT = 0.8
const FUZZY_WEIGHT = 0.7

export function stem(word) {
  if (word.length <= 3 || /^\d+$/.test(word)) return word
  const rules = [["ational", "ate"], ["ization", "ize"], ["ations", "ate"], ["ation", "ate"], ["ments", "ment"],
    ["ies", "y"], ["ing", ""], ["ed", ""], ["es", ""], ["ly", ""], ["s", ""]]
  for (const [suffix, replacement] of rules) {
    if (word.endsWith(suffix) && word.length - suffix.length >= 3 && !word.endsWith("ss")) {
      return word.slice(0, -suffix.length) + replacement
    }
  }
  return word
}

export function tokenize(text) {
  return (String(text).toLowerCase().match(/[a-z0-9]+/g) || [])
    .filter((token) => !STOPWORDS.has(token))
    .map(stem)
}

const SYNONYMS = new Map()
SYNONYM_GROUPS.forEach((group) => {
  const stems = [...new Set(group.map(stem))]
  stems.forEach((term) => SYNONYMS.set(term, [...new Set([...(SYNONYMS.get(term) || []), ...stems.filter((other) => other !== term)])]))
})

function editDistance(left, right, limit) {
  if (Math.abs(left.length - right.length) > limit) return limit + 1
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  for (let i = 1; i <= left.length; i++) {
    const current = [i]
    let rowMin = i
    for (let j = 1; j <= right.length; j++) {
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1))
      rowMin = Math.min(rowMin, current[j])
    }
    if (rowMin > limit) return limit + 1
    previous = current
  }
  return previous[right.length]
}

function normalise(vector) {
  const magnitude = Math.sqrt([...vector.values()].reduce((sum, value) => sum + value * value, 0))
  if (magnitude) vector.forEach((value, term) => vector.set(term, value / magnitude))
  return vector
}

// fields: [{ text, weight }]; the question carries more weight than the answer body.
export function buildIndex(documents) {
  const termCounts = documents.map(({ fields }) => {
    const counts = new Map()
    fields.forEach(({ text, weight = 1 }) => tokenize(text).forEach((term) => counts.set(term, (counts.get(term) || 0) + weight)))
    return counts
  })

  const documentFrequency = new Map()
  termCounts.forEach((counts) => counts.forEach((_, term) => documentFrequency.set(term, (documentFrequency.get(term) || 0) + 1)))
  const idf = new Map([...documentFrequency].map(([term, df]) => [term, Math.log(1 + documents.length / df)]))

  const vectors = termCounts.map((counts) => {
    const vector = new Map()
    counts.forEach((count, term) => vector.set(term, (1 + Math.log(count)) * idf.get(term)))
    return normalise(vector)
  })

  return { ids: documents.map(({ id }) => id), vectors, idf, vocabulary: [...idf.keys()] }
}

function addTerm(vector, term, weight, idf) {
  const value = weight * idf.get(term)
  if (value > (vector.get(term) || 0)) vector.set(term, value)
}

export function queryVector(query, index) {
  const vector = new Map()
  tokenize(query).forEach((term) => {
    if (index.idf.has(term)) addTerm(vector, term, EXACT_WEIGHT, index.idf)
    ;(SYNONYMS.get(term) || []).forEach((synonym) => index.idf.has(synonym) && addTerm(vector, synonym, SYNONYM_WEIGHT, index.idf))
    if (index.idf.has(term) || term.length < 3) return

    const prefixes = index.vocabulary.filter((word) => word.startsWith(term))
    if (prefixes.length) return prefixes.forEach((word) => addTerm(vector, word, PREFIX_WEIGHT, index.idf))

    const limit = term.length >= 6 ? 2 : 1
    if (term.length < 4) return
    // Only the closest spellings count, so "suport" means "support" rather than "report".
    const distances = index.vocabulary.map((word) => [word, editDistance(term, word, limit)]).filter(([, distance]) => distance <= limit)
    const closest = Math.min(...distances.map(([, distance]) => distance))
    distances.filter(([, distance]) => distance === closest).map(([word]) => word).forEach((word) => {
      addTerm(vector, word, FUZZY_WEIGHT, index.idf)
      ;(SYNONYMS.get(word) || []).forEach((synonym) => index.idf.has(synonym) && addTerm(vector, synonym, FUZZY_WEIGHT * SYNONYM_WEIGHT, index.idf))
    })
  })
  return normalise(vector)
}

// Returns matching entry ids, best first. Weak matches relative to the best one are dropped.
export function search(query, index) {
  const vector = queryVector(query, index)
  if (!vector.size) return []

  const scored = index.vectors.map((documentVector, position) => {
    let score = 0
    vector.forEach((weight, term) => { score += weight * (documentVector.get(term) || 0) })
    return { id: index.ids[position], score }
  }).sort((left, right) => right.score - left.score)

  const best = scored[0].score
  if (best < MIN_SCORE) return []
  return scored.filter(({ score }) => score >= MIN_SCORE && score >= best * RELATIVE_CUTOFF)
}
